import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useWorkspaceData } from "@/hooks/use-workspace-data";
/**
 * Rename folder dialog. Stage 2 only needs the chrome — the actual rename
 * API is phase-2.
 */
import { useEffect, useState } from "react";

export interface RenameFolderDialogProps {
  folderId: string | null;
  initialName?: string;
  onOpenChange: (open: boolean) => void;
}

export function RenameFolderDialog({
  folderId,
  initialName = "",
  onOpenChange,
}: RenameFolderDialogProps): JSX.Element {
  const [name, setName] = useState(initialName);
  // biome-ignore lint/correctness/useExhaustiveDependencies: folderId is included to invalidate when switching folders
  useEffect(() => setName(initialName), [initialName, folderId]);
  const renameFolder = useWorkspaceData((s) => s.renameFolder);

  async function submit(): Promise<void> {
    if (!folderId) return;
    try {
      await renameFolder({ id: folderId, name: name.trim() });
      onOpenChange(false);
    } catch {
      // phase_2 toast already shown
    }
  }

  return (
    <Dialog open={folderId !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename folder</DialogTitle>
          <DialogDescription>Rename only affects this folder, not its children.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="rename-folder-name">Name</Label>
            <Input
              id="rename-folder-name"
              autoFocus
              required
              minLength={1}
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
